import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { BRAND } from '@/data/site';

const NAV_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/services', label: 'Services' },
  { to: '/real-estate-partners', label: 'For Investors' },
  { to: '/hearth-project', label: 'Hearth Project' },
  { to: '/blog', label: 'Blog' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? 'backdrop-blur-xl bg-ohana-bg/85 border-b border-ohana-border'
          : 'bg-ohana-bg/0 border-b border-transparent'
      }`}
    >
      <div className="container-ohana flex items-center justify-between py-4">
        <Link
          to="/"
          data-testid="header-logo-link"
          className="flex items-center gap-2.5 group"
        >
          <img
            src="/ohana/logo.png"
            alt="Ohana Property & Transition Services LLC"
            className="h-11 w-11 rounded-full object-contain transition-transform group-hover:rotate-[-4deg] shadow-sm"
            data-testid="header-logo-image"
          />
          <span className="font-serif text-xl md:text-2xl text-ohana-primary leading-none">
            {BRAND.name}
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8" data-testid="header-nav">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive
                    ? 'text-ohana-primary'
                    : 'text-ohana-text-muted hover:text-ohana-primary'
                }`
              }
              end={l.to === '/'}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link to="/contact" className="btn-primary !py-3 !px-6 text-sm" data-testid="header-cta-quote">
            Get a Free Quote
          </Link>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden grid h-10 w-10 place-items-center rounded-full border border-ohana-border bg-white text-ohana-primary"
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-ohana-border bg-ohana-bg" data-testid="mobile-menu">
          <div className="container-ohana py-6 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === '/'}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `py-3 px-4 rounded-xl text-base font-medium ${
                    isActive
                      ? 'bg-ohana-surface text-ohana-primary'
                      : 'text-ohana-text hover:bg-ohana-surface'
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              className="btn-primary mt-4 w-full"
              data-testid="mobile-cta-quote"
            >
              Get a Free Quote
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
